import { api, type ApiResponse } from './api'

/**
 * Cliente de la bandeja de seguimientos.
 *
 * Los seguimientos no tienen tabla propia: son la fecha de próximo contacto de
 * una cotización más su última nota, y ya viven en `quotes` y `quote_notes`.
 * Este módulo solo las agrupa y las presenta para que el comercial tenga una
 * lista de lo que tiene que hacer hoy, en vez de recorrer todas las cotizaciones
 * buscándolas a mano.
 */

/**
 * Cubo de seguimiento.
 *
 * Los calcula el servidor y no el cliente. Comparar fechas en el cliente es la
 * forma más fácil de tener un desfase de un día por zona horaria, y un seguimiento
 * que aparece vencido cuando no toca es peor que uno que no aparece.
 */
export type FollowUpBucket = 'vencido' | 'hoy' | 'proximo' | 'sin_fecha'

export const FOLLOW_UP_BUCKETS: ReadonlyArray<{ value: FollowUpBucket; label: string }> = [
  { value: 'vencido', label: 'Vencidos' },
  { value: 'hoy', label: 'Hoy' },
  { value: 'proximo', label: 'Próximos' },
  { value: 'sin_fecha', label: 'Sin fecha' },
]

export const FOLLOW_UP_BUCKET_LABELS: Record<FollowUpBucket, string> = {
  vencido: 'Vencido',
  hoy: 'Hoy',
  proximo: 'Próximo',
  sin_fecha: 'Sin fecha',
}

export interface FollowUpItem {
  quote_id: number
  customer_name: string
  document: string | null
  status: string
  total_price: string
  next_follow_up_date: string | null
  last_follow_up_note: string | null
  unread_notes_count: number
  notes_count: number
  bucket: FollowUpBucket
  is_overdue: boolean
}

export interface FollowUpNote {
  id: number
  note: string
  type: string
  user_name: string | null
  next_follow_up_date: string | null
  created_at: string | null
}

export interface FollowUpCounts {
  overdue: number
  today: number
  upcoming: number
  unread: number
}

export interface FollowUpMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  counts: FollowUpCounts
}

export interface FollowUpPage {
  data: FollowUpItem[]
  meta: FollowUpMeta
}

export interface FollowUpListParams {
  search?: string
  bucket?: FollowUpBucket
  status?: string
  page?: number
  per_page?: number
}

function withoutEmpty<T extends object>(params: T): Partial<T> {
  const cleaned: Partial<T> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    cleaned[key as keyof T] = value as T[keyof T]
  }
  return cleaned
}

export const followUpsApi = {
  list(params: FollowUpListParams = {}) {
    return api.get<FollowUpPage>('/follow-ups', {
      params: withoutEmpty(params),
    })
  },

  notes(quoteId: number) {
    return api.get<ApiResponse<FollowUpNote[]>>(`/follow-ups/${quoteId}/notes`)
  },
}
